import { useAge, useTranslation } from '@core/i18n';

interface RelativeTimeProps {
  /** ISO-8601. */
  value: string | null | undefined;
  /** Milliseconds, from the shared clock, so every age on screen agrees. */
  nowMs: number;
  className?: string;
  /** Shown when there is no timestamp. "Never" is a different claim from "0 minutes ago". */
  fallback?: string;
}

/**
 * How long ago something happened, with the exact time on hover.
 *
 * <p>Relative as the primary reading because on a ten-minute duty cycle "where it is"
 * always means "where it was", and an absolute timestamp makes the reader do that
 * subtraction themselves. The `title` keeps the precise value one hover away for anyone
 * correlating with a log.
 */
export function RelativeTime({ value, nowMs, className, fallback = '—' }: RelativeTimeProps) {
  const age = useAge();
  const { fmt } = useTranslation();

  if (!value) {
    return <span className={className}>{fallback}</span>;
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return <span className={className}>{fallback}</span>;
  }

  /*
   * `useAge` owns the whole scale, forwards and backwards.
   *
   * <p>This component used to call the English-only `formatAge` and carry its own
   * future-tense wrapper, which is how a Slovenian tracker list ended up reading
   * "Nazadnje viden · just now". Passing the signed delta through hands both directions to
   * the catalogue, where a language that inflects the noun can say so — and it deletes the
   * duplicate rule about what counts as clock skew rather than the future.
   */
  return (
    <time dateTime={value} title={fmt.dateTime(parsed)} className={className}>
      {age(nowMs - parsed) ?? fallback}
    </time>
  );
}
